import React, { useState, useEffect } from 'react';
import styles from './jiraSettings.module.css';
import { Form, Row, Col, Button, Select, Switch, message, Input, Alert } from 'antd';
import { MinusCircleOutlined, ExportOutlined } from '@ant-design/icons';
import { get, post } from '../../utils/fetch';
import { ConnectPrompt } from './ConnectPrompt';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };
const BUGHERD_PRIORITIES: OptionType[] = [
  { value: '1', label: 'Critical' },
  { value: '2', label: 'Important' },
  { value: '3', label: 'Normal' },
  { value: '4', label: 'Minor' }
];

const defaultPriorityMappings = (
  jiraPriorities: OptionType[]
): Record<string, string> => {
  if (jiraPriorities.length === 0) return {};

  const lastIndex = jiraPriorities.length - 1;
  const indexes = [
    0,
    Math.min(1, lastIndex),
    Math.round(lastIndex / 2),
    lastIndex
  ];

  return BUGHERD_PRIORITIES.reduce((mappings, priority, index) => {
    mappings[priority.value] = jiraPriorities[indexes[index]].value;
    return mappings;
  }, {} as Record<string, string>);
};

interface CustomFieldMapping {
  key: string | null;
  value: string | null;
}
type CustomFieldOption = {
  label: string;
  value: {
    id: string;
    key: string;
    name: string;
    type: string;
    custom: boolean;
    required: boolean;
    allowed_values: {
      allowed_values: {
        id: string;
        value: string;
      }[];
    } | null;
  };
};
interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          site_id: OptionType;
          project: OptionType;
          issue_type: OptionType;
          epic: OptionType;
          jira_done_column: OptionType;
          new_tasks: boolean;
          sync_task_updates: boolean;
          new_comments: boolean;
          sync_jira_comments: boolean;
          sync_jira_task_completion: boolean;
          sync_jira_status: boolean;
          sync_jira_priority: boolean;
          sync_custom_fields: boolean;
          custom_fields: Record<string, string>;
          status_mapping: Record<string, string>;
          priority_mapping: Record<string, string>;
          jira_priorities: OptionType[];
          done_column: string;
          task_status: OptionType;
        };
      }
    | undefined;
  onDisconnect: () => void;
}

const JiraSettings: React.FC<Props> = (props: Props) => {
  const [form] = Form.useForm();

  const { integration, organizationId, projectId } = props;

  const [syncPaused, setSyncPaused] = useState(false);
  const [changingConnection, setChangingConnection] = useState(false);

  const {
    integrationId,
    projectIntegrationId,
    isEnabled,
    getConnectedState,
    saveSettings
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Jira',
    onConnectedStateLoaded: results => setSyncPaused(results.settings?.sync_paused === true)
  });

  const changeConnection = async () => {
    if (!projectIntegrationId || !integrationId) return;
    setChangingConnection(true);
    try {
      await post(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/${syncPaused ? 'resume' : 'disconnect'}`,
        { id: projectIntegrationId }
      );
      setSyncPaused(!syncPaused);
      message.success(syncPaused ? 'Jira syncing resumed.' : 'Jira disconnected. Settings and task connections have been preserved.');
    } catch (error) {
      message.error('Could not change Jira syncing. Please try again.');
    } finally {
      setChangingConnection(false);
    }
  };

  const [selectedSiteId, setSelectedSiteId] = useState(
    integration?.settings?.site_id
  );
  const [selectedProject, setSelectedProject] = useState(
    integration?.settings?.project
  );
  const [selectedIssueType, setSelectedIssueType] = useState(
    integration?.settings?.issue_type
  );
  const [selectedEpic, setSelectedEpic] = useState(
    integration?.settings?.epic
  );
  const [selectedJiraDoneColumn, setSelectedJiraDoneColumn] = useState(
    integration?.settings?.jira_done_column
  );
  const [selectedStatus, setSelectedStatus] = useState(
    integration?.settings?.task_status
  );
  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [syncTaskUpdates, setSyncTaskUpdates] = useState(
    integration?.settings?.sync_task_updates ?? true
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );
  const [syncCommentsToJira, setSyncCommentsToJira] = useState(
    integration?.settings?.sync_jira_comments ?? false
  );
  const [syncTaskCompletion, setSyncTaskCompletion] = useState(
    integration?.settings?.sync_jira_task_completion ?? false
  );
  const [syncJiraStatus, setSyncJiraStatus] = useState(
    integration?.settings?.sync_jira_status ?? false
  );
  const [syncJiraPriority, setSyncJiraPriority] = useState(
    integration?.settings?.sync_jira_priority ?? false
  );
  const [doneColumn, setDoneColumn] = useState(
    integration?.settings?.done_column
  );

  const [isCustomFieldsEnabled, setIsCustomFieldsEnabled] = useState(
    integration?.settings?.sync_custom_fields ?? false
  );
  const [customFieldMappings, setCustomFieldMappings] = useState<CustomFieldMapping[]>([]);
  const [statusMappings, setStatusMappings] = useState<Record<string, string>>({});
  const [priorityMappings, setPriorityMappings] = useState<Record<string, string>>(
    integration?.settings?.priority_mapping || {}
  );

  useEffect(() => {
    if (integration?.settings?.status_mapping) {
      // Invert the mapping: backend is {jiraStatusId: bugherdColumnId} -> UI needs {bugherdColumnId: jiraStatusId}
      const invertedMappings = Object.entries(integration.settings.status_mapping).reduce((acc, [jiraId, bhId]) => {
        acc[bhId] = jiraId;
        return acc;
      }, {} as Record<string, string>);
      setStatusMappings(invertedMappings);
    }
  }, [integration]);
  const [remoteCustomFieldOptions, setRemoteCustomFieldOptions] = useState<CustomFieldOption[]>([]);
  const [isCustomFieldsLoading, setIsCustomFieldsLoading] = useState(false);


  const [remoteSiteIdOptions, setRemoteSiteIdOptions] = useState([]);
  const [remoteProjectOptions, setRemoteProjectOptions] = useState([]);
  const [remoteIssueTypeOptions, setRemoteIssueTypeOptions] = useState([]);
  const [remoteEpicOptions, setRemoteEpicOptions] = useState([]);
  const [
    remoteJiraDoneColumnOptions,
    setRemoteJiraDoneColumnOptions
  ] = useState<OptionType[]>([]);
  const [remoteStatusOptions, setRemoteStatusOptions] = useState<OptionType[]>(
    []
  );
  const [remotePriorityOptions, setRemotePriorityOptions] = useState<OptionType[]>(
    integration?.settings?.jira_priorities || []
  );

  const [isSiteIdLoading, setIsSiteIdLoading] = useState(false);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isIssueTypesLoading, setIsIssueTypesLoading] = useState(false);
  const [isEpicsLoading, setIsEpicsLoading] = useState(false);
  const [isJiraDoneColumnLoading, setIsJiraDoneColumnLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isPrioritiesLoading, setIsPrioritiesLoading] = useState(false);

  const getSiteIds = async () => {
    if (!integration?.enabled) return;
    setIsSiteIdLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/jira_site_ids?id=${integration.id}`
      );
      setRemoteSiteIdOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira Site IDs. Please try again.');
    } finally {
      setIsSiteIdLoading(false);
    }
  };

  const getProjects = async () => {
    if (!integration?.enabled || !selectedSiteId) return;
    setIsProjectsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=${selectedSiteId.value}`
      );
      setRemoteProjectOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira projects. Please try again.');
    } finally {
      setIsProjectsLoading(false);
    }
  };

  const getIssueTypes = async currentSelectedProject => {
    if (
      !integration?.enabled ||
      !currentSelectedProject?.value ||
      !selectedSiteId
    )
      return;
    setIsIssueTypesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/jira_issue_types?id=${integration.id}&project_id=${currentSelectedProject.value}&site_id=${selectedSiteId.value}`
      );
      setRemoteIssueTypeOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira issue types. Please try again.');
    } finally {
      setIsIssueTypesLoading(false);
    }
  };

  const getEpics = async currentSelectedProject => {
    if (
      !integration?.enabled ||
      !currentSelectedProject?.value ||
      !selectedSiteId
    )
      return;
    setIsEpicsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/jira_epics?id=${integration.id}&project_id=${currentSelectedProject.value}&site_id=${selectedSiteId.value}`
      );
      setRemoteEpicOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira epics. Please try again.');
    } finally {
      setIsEpicsLoading(false);
    }
  };

  const getJiraStatuses = async currentSelectedProject => {
    if (
      !integration?.enabled ||
      !currentSelectedProject?.value ||
      !selectedSiteId
    )
      return;
    setIsJiraDoneColumnLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/jira_statuses?id=${integration.id}&project_id=${currentSelectedProject.value}&site_id=${selectedSiteId.value}`
      );
      setRemoteJiraDoneColumnOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira statuses. Please try again.');
    } finally {
      setIsJiraDoneColumnLoading(false);
    }
  };

  const getJiraPriorities = async (
    siteId: string,
    initializeMappings: boolean
  ) => {
    if (!integration?.enabled || !siteId) return;

    setIsPrioritiesLoading(true);
    try {
      const results: OptionType[] = await get(
        `/organizations/${organizationId}/integrations/jira_priorities?id=${integration.id}&site_id=${siteId}`
      );
      setRemotePriorityOptions(results);
      if (initializeMappings) {
        setPriorityMappings(defaultPriorityMappings(results));
      }
    } catch (error) {
      message.error('Failed to fetch Jira priorities. Please try again.');
    } finally {
      setIsPrioritiesLoading(false);
    }
  };

  const getCustomFields = async () => {
    if (
      !integration?.enabled ||
      !selectedSiteId?.value ||
      !selectedProject?.value ||
      !selectedIssueType?.value
    )
      return;

    setIsCustomFieldsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/jira_custom_fields?id=${integration.id}&site_id=${selectedSiteId.value}&project_id=${selectedProject.value}&issue_type_id=${selectedIssueType.value}`
      );
      setRemoteCustomFieldOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira custom fields. Please try again.');
    } finally {
      setIsCustomFieldsLoading(false);
    }
  };

  const getStatusColumns = async (currentProjectId: string) => {
    if (!integration?.enabled) return;
    setIsStatusLoading(true);
    try {
      const results = await get(`/projects/full?id=${currentProjectId}`);
      const columns: OptionType[] = [
        ...results.columns.map(({ id, name }) => ({
          value: String(id),
          label: name
        }))
      ];
      setRemoteStatusOptions(columns);
    } catch (error) {
      message.error('Failed to fetch status columns. Please try again.');
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleSiteIdChange = (
    _value: string | undefined,
    option: OptionType | undefined
  ) => {
    setSelectedSiteId(option || null);
    setStatusMappings({});
    setPriorityMappings({});
    setRemotePriorityOptions([]);

    if (!option) {
      setSelectedProject(undefined);
      setSelectedIssueType(undefined);
      setRemoteProjectOptions([]);
      setRemoteIssueTypeOptions([]);
      form.setFieldsValue({ project: undefined, issue_type: undefined });
    } else {
      getProjects(option.value);
      getJiraPriorities(option.value, true);
    }
  };

  const handleProjectChange = (
    _value: string | undefined,
    option: OptionType | undefined
  ) => {
    setSelectedProject(option || null);
    setStatusMappings({});

    if (!option) {
      setSelectedIssueType(undefined);
      setRemoteIssueTypeOptions([]);
      form.setFieldsValue({ issue_type: undefined });
    } else {
      getIssueTypes(option);
      getEpics(option);
      getJiraStatuses(option);
    }
  };

  const handleEpicChange = (_value: string | undefined, option: OptionType | undefined) => {
    setSelectedEpic(option || {});

    if (!option) {
      setSelectedEpic({});
      setRemoteEpicOptions([]);
      form.setFieldsValue({ epic: undefined });
    }
  };

  const handleIssueTypeChange = (
    _value: string | undefined,
    option: OptionType | undefined
  ) => {
    setSelectedIssueType(option || null);
  };

  const handleStatusChange = (
    value: string | undefined,
    option: OptionType | undefined
  ) => {
    setSelectedStatus(option || null);
  };

  const handleDoneColumnChange = (value: string | undefined) => {
    setDoneColumn(value);
  };

  const handleJiraDoneColumnChange = (
    _value: string | undefined,
    option: OptionType | undefined
  ) => {
    setSelectedJiraDoneColumn(option || null);
  };

  const handleStatusMappingChange = (bugherdColumnId: string, jiraStatusId?: string) => {
    setStatusMappings({
      ...statusMappings,
      [bugherdColumnId]: jiraStatusId || ''
    });
  };

  const handlePriorityMappingChange = (
    bugherdPriorityId: string,
    jiraPriorityId?: string
  ) => {
    setPriorityMappings({
      ...priorityMappings,
      [bugherdPriorityId]: jiraPriorityId || ''
    });
  };

  const jiraStatusOptionsForColumn = (bugherdColumnId: string) => {
    const selectedJiraStatusIds = Object.entries(statusMappings)
      .filter(([mappedBugherdColumnId, jiraStatusId]) =>
        mappedBugherdColumnId !== bugherdColumnId && jiraStatusId
      )
      .map(([_mappedBugherdColumnId, jiraStatusId]) => jiraStatusId);

    return remoteJiraDoneColumnOptions.filter(
      (option: OptionType) => !selectedJiraStatusIds.includes(option.value)
    );
  };

  const handleKeyChange = (index: number, key: string | null) => {
    const newMappings = [...customFieldMappings];
    newMappings[index].key = key;
    newMappings[index].value = null;
    setCustomFieldMappings(newMappings);
  };

  const handleMappingChange = (
    index: number,
    field: 'key' | 'value',
    val: any | null
  ) => {
    const newMappings = [...customFieldMappings];
    newMappings[index][field] = val;
    setCustomFieldMappings(newMappings);
  };

  const addMappingRow = () => {
    setCustomFieldMappings([...customFieldMappings, { key: null, value: '' }]);
  };

  const removeMappingRow = (index: number) => {
    const newMappings = customFieldMappings.filter((_, i) => i !== index);
    setCustomFieldMappings(newMappings);
  };

  const handleSubmit = () => {
    const customFieldsPayload = customFieldMappings.reduce((acc, field) => {
      if (field.key && field.value) acc[field.key] = field.value;
      return acc;
    }, {} as Record<string, string>);

    const validBugherdColumnIds = remoteStatusOptions.map(
      (opt: any) => opt.value
    );
    const hasLoadedOptions = validBugherdColumnIds.length > 0;

    // Invert back for backend: {bugherdColumnId: jiraStatusId} -> {jiraStatusId: bugherdColumnId}
    const cleanedStatusMappings = Object.keys(statusMappings).reduce(
      (acc, bugherdColumnId) => {
        const jiraStatusId = statusMappings[bugherdColumnId];
        if (!jiraStatusId || String(jiraStatusId) === '0') return acc;
        if (
          hasLoadedOptions &&
          !validBugherdColumnIds.includes(bugherdColumnId)
        )
          return acc;
        acc[jiraStatusId] = bugherdColumnId;
        return acc;
      },
      {} as Record<string, string>
    );

    const validJiraPriorityIds = remotePriorityOptions.map(
      priority => priority.value
    );
    const cleanedPriorityMappings = Object.entries(priorityMappings).reduce(
      (mappings, [bugherdPriorityId, jiraPriorityId]) => {
        if (
          jiraPriorityId &&
          validJiraPriorityIds.includes(jiraPriorityId)
        ) {
          mappings[bugherdPriorityId] = jiraPriorityId;
        }
        return mappings;
      },
      {} as Record<string, string>
    );

    return saveSettings(
      {
        site_id: selectedSiteId,
        project: selectedProject,
        issue_type: selectedIssueType,
        epic: selectedEpic,
        new_tasks: autoPost,
        sync_task_updates: syncTaskUpdates,
        new_comments: newComments,
        sync_jira_comments: syncCommentsToJira,
        sync_jira_task_completion: syncTaskCompletion,
        sync_jira_status: syncJiraStatus,
        sync_jira_priority: syncJiraPriority,
        sync_custom_fields: isCustomFieldsEnabled,
        custom_fields: customFieldsPayload,
        status_mapping: cleanedStatusMappings,
        priority_mapping: cleanedPriorityMappings,
        jira_priorities: remotePriorityOptions,
        task_status: selectedStatus
      },
      { form }
    );
  };

  useEffect(() => {
    if (integration?.enabled) {
      getConnectedState();
      getProjects();
      getSiteIds();
      getStatusColumns(projectId);
      getJiraStatuses(projectId);
      if (
        selectedSiteId?.value &&
        (integration.settings.jira_priorities || []).length === 0
      ) {
        getJiraPriorities(
          selectedSiteId.value,
          Object.keys(integration.settings.priority_mapping || {}).length === 0
        );
      }
    }
  }, [integration, projectId, organizationId]);

  useEffect(() => {
    if (selectedProject?.value) {
      getIssueTypes(selectedProject);
      getEpics(selectedProject);
      getJiraStatuses(selectedProject);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedSiteId?.value) {
      getProjects();
    }
  }, [selectedSiteId]);

  useEffect(() => {
    if (integration?.settings?.custom_fields) {
      const initialMappings = Object.entries(
        integration.settings.custom_fields
      ).map(([key, value]) => ({
        key,
        value: value,
      }));
      setCustomFieldMappings(initialMappings);
    }
  }, [integration]);

  useEffect(() => {
    if (isCustomFieldsEnabled) {
      getCustomFields();
    }
  }, [isCustomFieldsEnabled, selectedProject, selectedIssueType]);

  const renderStatusMappings = () => {
    return (
      <div className={styles.statusMappings}>
        <Row className={styles.statusMappingRow} gutter={16}>
          <Col span={11}><strong>BugHerd Status</strong></Col>
          <Col span={2}></Col>
          <Col span={11}><strong>Jira Status</strong></Col>
        </Row>
        {remoteStatusOptions.map((bhStatus: any) => (
          <Row key={bhStatus.value} className={styles.statusMappingRow} gutter={16} align="middle">
            <Col span={11}>
              <span className={styles.jiraStatusLabel}>{bhStatus.label}</span>
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <span>↔</span>
            </Col>
            <Col span={11}>
              <Select
                className={styles.bugherdColumnSelect}
                placeholder="Select Jira status"
                value={statusMappings[bhStatus.value] || undefined}
                onChange={(value) => handleStatusMappingChange(bhStatus.value, value)}
                options={jiraStatusOptionsForColumn(bhStatus.value)}
                allowClear
              />
            </Col>
          </Row>
        ))}
      </div>
    );
  };

  const renderPriorityMappings = () => {
    return (
      <div className={styles.statusMappings}>
        <Row className={styles.statusMappingRow} gutter={16}>
          <Col span={11}><strong>BugHerd Severity</strong></Col>
          <Col span={2}></Col>
          <Col span={11}><strong>Jira Priority</strong></Col>
        </Row>
        {BUGHERD_PRIORITIES.map(priority => (
          <Row
            key={priority.value}
            className={styles.statusMappingRow}
            gutter={16}
            align="middle"
          >
            <Col span={11}>
              <span className={styles.jiraStatusLabel}>{priority.label}</span>
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <span>→</span>
            </Col>
            <Col span={11}>
              <Select
                className={styles.bugherdColumnSelect}
                placeholder="Select Jira priority"
                value={priorityMappings[priority.value] || undefined}
                onChange={value =>
                  handlePriorityMappingChange(priority.value, value)
                }
                options={remotePriorityOptions}
                loading={isPrioritiesLoading}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Col>
          </Row>
        ))}
      </div>
    );
  };

  const renderCustomFieldMappings = () => {
    if (customFieldMappings.length === 0) addMappingRow();

    return (
      <div className={styles.customFieldMappings}>
        {customFieldMappings.map((mapping, index) => {
          const selectedFieldDetails = remoteCustomFieldOptions.find(
            opt => opt.value.key === mapping.key
          );

          let valueInput = (
            <Input className={styles.customFieldSelect} placeholder="Select a field first" disabled />
          );

          if (selectedFieldDetails) {
            const fieldType = selectedFieldDetails.value.type;

            if (fieldType === 'string') {
              valueInput = (
                <Input
                  className={styles.customFieldSelect}
                  placeholder="Enter value"
                  value={mapping.value}
                  onChange={e =>
                    handleMappingChange(index, 'value', e.target.value)
                  }
                />
              );
            } else if (fieldType === 'option') {
              const options =
                selectedFieldDetails.value.allowed_values?.map(
                  allowedVal => ({
                    label: allowedVal.value,
                    value: allowedVal.id,
                  })
                ) || [];

              valueInput = (
                <Select
                  className={styles.customFieldSelect}
                  placeholder="Select a value"
                  value={mapping.value && mapping.value.id}
                  onChange={value =>
                    handleMappingChange(index, 'value', { 'id': value })
                  }
                  options={options}
                  showSearch
                  optionFilterProp="label"
                />
              );
            }
          }

          return (
            <Row
              key={index}
              className={styles.customFieldMappingRow}
              gutter={16}
              align="middle"
            >
              <Col span={10}>
                <Select
                  className={styles.customFieldSelect}
                  placeholder="Select a Jira custom field"
                  value={mapping.key}
                  onChange={value => handleKeyChange(index, value)}
                  loading={isCustomFieldsLoading}
                  showSearch
                  optionFilterProp="label"
                >
                  {remoteCustomFieldOptions
                    .filter(
                      option =>
                        option.value.custom &&
                        ['option', 'string'].includes(option.value.type)
                    )
                    .map(option => (
                      <Select.Option
                        key={option.value.key}
                        value={option.value.key}
                        label={option.label}
                      >
                        {option.value.required ? '*' : ''} {option.label}
                      </Select.Option>
                    ))}
                </Select>
              </Col>
              <Col span={2} style={{ textAlign: 'center' }}>
                <span>↔</span>
              </Col>
              <Col span={10}>{valueInput}</Col>
              <Col span={1} style={{ textAlign: 'right' }}>
                <Button
                  type="text"
                  icon={<MinusCircleOutlined />}
                  onClick={() => removeMappingRow(index)}
                  danger
                />
              </Col>
            </Row>
          );
        })}
        <br />
        <a onClick={addMappingRow}>
          + Add another field
        </a>
      </div>
    );
  };

  const renderIntegrationSettings = () => {
    return (
    <div>
      <Form
        form={form}
        layout="horizontal"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
      >
        <div className={styles.jiraRequired}>
          <div className={styles.requiredBubble}>REQUIRED</div>
          <h3>Connect your current workflow</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="site_id"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>Jira Domain
                  </span>
                }
                className={styles.jiraSiteId}
                rules={[
                  { required: true, message: 'Please select a Jira domain.' }
                ]}
                initialValue={selectedSiteId?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a domain"
                  optionFilterProp="label"
                  options={remoteSiteIdOptions}
                  onChange={handleSiteIdChange}
                  loading={isSiteIdLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          {selectedSiteId && (
            <div>
              <Row className={styles.jiraEditRow}>
                <Col span={24}>
                  <Form.Item
                    name="project"
                    label={
                      <span className={styles.requiredLabel}>
                        <span>*</span>Jira Project
                      </span>
                    }
                    className={styles.jiraProject}
                    rules={[
                      {
                        required: true,
                        message: 'Please select a Jira project.'
                      }
                    ]}
                    initialValue={selectedProject?.value}
                  >
                    <Select
                      className={styles.jiraSelect}
                      showSearch
                      placeholder="Select a project"
                      optionFilterProp="label"
                      options={remoteProjectOptions}
                      onChange={handleProjectChange}
                      loading={isProjectsLoading}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}
        </div>

        {selectedProject && (
          <div className={styles.jiraWorkflows}>
            <h3>Configure Jira Issue Attributes</h3>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="issue_type"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>Jira Issue Type
                    </span>
                  }
                  className={styles.jiraIssueType}
                  rules={[
                    {
                      required: true,
                      message: 'Please select an issue type.'
                    }
                  ]}
                  initialValue={selectedIssueType?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select an issue type"
                    optionFilterProp="label"
                    options={remoteIssueTypeOptions}
                    onChange={handleIssueTypeChange}
                    loading={isIssueTypesLoading}
                    disabled={!selectedProject}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="epic"
                  label={
                    <span className={styles.requiredLabel}>
                      Jira Epic
                    </span>
                  }
                  className={styles.jiraEpic}
                  initialValue={selectedEpic?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select an epic"
                    optionFilterProp="label"
                    onChange={handleEpicChange}
                    options={remoteEpicOptions}
                    loading={isEpicsLoading}
                    disabled={!selectedProject}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  label="Link Custom Field"
                  name="customFields"
                  className={styles.customFields}
                >
                  <Switch
                    checked={isCustomFieldsEnabled}
                    onChange={e => setIsCustomFieldsEnabled(e)}
                    disabled={!selectedProject || !selectedIssueType}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                {isCustomFieldsEnabled && renderCustomFieldMappings()}
              </Col>
            </Row>
          </div>
        )}

        <div className={styles.jiraWorkflows}>
          <h3>Choose your desired workflow</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                label="Automatically post new tasks to Jira"
                className={styles.autoPost}
              >
                <Switch checked={autoPost} onChange={e => setAutoPost(e)} />
              </Form.Item>
            </Col>
            {autoPost && (
              <Col span={24}>
                <Form.Item
                  name="statusColumn"
                  label="Create task when status is equal to"
                  className={styles.selectStatus}
                  initialValue={selectedStatus?.value}
                  tooltip="This is an optional field. Select a status to trigger task creation in Jira."
                >
                  <Select
                    placeholder="Select a status"
                    options={remoteStatusOptions}
                    onChange={handleStatusChange}
                    loading={isStatusLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item label="Automatically sync task detail updates to Jira">
                <Switch
                  checked={syncTaskUpdates}
                  onChange={setSyncTaskUpdates}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.newComments}
                label="Automatically send new BugHerd comments to Jira"
              >
                <Switch checked={newComments} onChange={e => setNewComments(e)} />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncComments}
                label="Sync comments from Jira to BugHerd"
              >
                <Switch
                  checked={syncCommentsToJira}
                  onChange={e => setSyncCommentsToJira(e)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncStatus}
                label="Sync status updates between Jira and BugHerd"
              >
                <Switch
                  checked={syncJiraStatus}
                  onChange={e => setSyncJiraStatus(e)}
                />
              </Form.Item>
            </Col>
          </Row>
          {syncJiraStatus && (
            <div className={styles.jiraEditRow}>
              {renderStatusMappings()}
            </div>
          )}
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncPriority}
                label="Sync BugHerd severity to Jira priority"
              >
                <Switch
                  checked={syncJiraPriority}
                  onChange={setSyncJiraPriority}
                  disabled={!selectedSiteId || !selectedIssueType}
                />
              </Form.Item>
            </Col>
          </Row>
          {syncJiraPriority && (
            <div className={styles.jiraEditRow}>
              {renderPriorityMappings()}
            </div>
          )}
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncUsers}
                label="Sync users between Jira and BugHerd"
              >
                <a
                  href={`/settings/organizations/${organizationId}/team?manage_jira_users=true&integration_id=${integration?.id}&project_integration_id=${projectIntegrationId}&site_id=${selectedSiteId?.value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.manageLink}
                >
                  Manage <ExportOutlined style={{ marginLeft: '5px' }} />
                </a>
              </Form.Item>
            </Col>
          </Row>
        </div>
        <Row className={styles.jiraEditRow} justify="space-between" align="middle">
          <Col>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                className={styles.save}
              >
                Save
              </Button>
            </Form.Item>
          </Col>
          <Col>
            <Button
              type="text"
              shape="round"
              onClick={changeConnection}
              loading={changingConnection}
              disabled={!projectIntegrationId}
              className={styles.disconnectButton}
              danger
            >
              {syncPaused ? 'Resume syncing' : 'Disconnect'}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  )};

  return (
    <div className={styles.container}>
      {isEnabled ? (
        <>
          {syncPaused && (
            <Alert
              className={styles.jiraPausedNotice}
              type="warning"
              message="Jira syncing is paused"
              description="Your settings and existing task connections are preserved. Resume syncing to reconnect this project."
            />
          )}
          {renderIntegrationSettings()}
        </>
      ) : (
        <ConnectPrompt
          integrationName="jira"
          projectId={projectId}
          organizationId={organizationId}
          type="Jira"
          display="Jira"
          provider="atlassian_oauth2"
        />
      )}
    </div>
  );
};

export default (props: Props) => <JiraSettings {...props} />;
